import "./styles.scss";

import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import classnames from "classnames";
import moment from "moment";

import DropdownCard from "components/DropdownCard";
import CalendarMonths from "./components/Months";
import CalendarDays from "./components/Days";

import useClickOutside from "libs/helpers/useClickOutside";
import useDropdownPlacement from "hooks/useDropdownPlacement";
import { ReactComponent as ExitIcon } from "assets/icons/cross.svg";
import { ReactComponent as CalendarIcon } from "assets/icons/new/calendar.svg";

const mainClass = "calendar";

const Calendar = ({
  label,
  default_value,
  only_icon,
  close_icon,
  required,
  error,
  anchor,
  touched,
  prev_date,
  next_date,
  autoPlacement,
  onChange,
}) => {
  const ref = useRef();
  const trigger_ref = useRef();
  const dropdown_ref = useRef();
  const { t } = useTranslation();

  const [date, setDate] = useState(moment());
  const [calendar_open, setCalendarOpen] = useState(false);
  const open_top = useDropdownPlacement(trigger_ref, calendar_open, {
    dropdownRef: dropdown_ref,
    forceBottom: !autoPlacement,
  });
  const [selected_month, setSelectedMonth] = useState(false);
  const [selected_date, setSelectedDate] = useState(
    !!default_value ? moment(default_value) : null,
  );

  useClickOutside(ref, () => {
    setDate(moment());
    setCalendarOpen(false);
    setSelectedMonth(null);
  });

  const onMonthChange = (month) => {
    setDate((prev) => moment(prev).set("month", month));
    setSelectedMonth(true);
  };

  const onSubmit = (day) => {
    const selected_date = moment(date).set("date", day);

    setDate(moment());
    setCalendarOpen(false);
    setSelectedDate(selected_date);
    setSelectedMonth(null);

    if (typeof onChange === "function")
      onChange(moment(selected_date).toISOString());
  };

  const onClear = (e) => {
    e?.stopPropagation();

    setSelectedDate(null);

    if (typeof onChange === "function") onChange(null);
  };

  return (
    <div
      ref={ref}
      className={classnames(mainClass, {
        [`${mainClass}--only-icon`]: only_icon,
      })}
    >
      {!only_icon && (
        <label>
          {label}
          {!!required && <span>*</span>}
        </label>
      )}
      <div className={`${mainClass}__field`}>
        {!only_icon ? (
          <button
            ref={trigger_ref}
            type="button"
            onClick={() =>
              setCalendarOpen((prev) => {
                if (prev) {
                  setSelectedMonth(false);
                }
                return !prev;
              })
            }
            className={classnames(`${mainClass}__button`, {
              [`${mainClass}__button--active`]: !!selected_date,
              [`${mainClass}__button--focus`]: !!calendar_open,
              [`${mainClass}__button--error`]:
                !!error && Object.keys(touched).length !== 0,
            })}
          >
            {selected_date && !!selected_date?._isValid ? (
              <>{moment(selected_date).format("ll")}</>
            ) : (
              t("Select date")
            )}
            <span
              role="presentation"
              className={`${mainClass}__button__aside-button`}
              onClick={onClear}
            >
              {selected_date && !!selected_date?._isValid ? (
                <ExitIcon />
              ) : (
                <CalendarIcon />
              )}
            </span>
          </button>
        ) : (
          <button
            ref={trigger_ref}
            className={`${mainClass}__icon`}
            type="button"
            onClick={() =>
              setCalendarOpen((prev) => {
                if (prev) {
                  setSelectedMonth(false);
                }
                return !prev;
              })
            }
          >
            <CalendarIcon />
          </button>
        )}
        {!!calendar_open && (
          <div
            ref={dropdown_ref}
            className={classnames(
              `${mainClass}__content`,
              `${mainClass}__content--${!!!selected_month ? "months" : "days"}`,
              {
                [`${mainClass}__content--${anchor}`]: !!anchor,
                [`${mainClass}__content--top`]: open_top,
              },
            )}
          >
            <DropdownCard position="left">
              {close_icon && (
                <button
                  className={`${mainClass}__close`}
                  type="button"
                  onClick={() => setCalendarOpen(false)}
                >
                  {t("Close")}
                </button>
              )}
              {!!!selected_month ? (
                <CalendarMonths
                  date={date}
                  setDate={setDate}
                  selected_date={selected_date}
                  prev_date={prev_date}
                  next_date={next_date}
                  onSelect={onMonthChange}
                />
              ) : (
                <CalendarDays
                  date={date}
                  setDate={setDate}
                  selected_date={selected_date}
                  prev_date={prev_date}
                  next_date={next_date}
                  onSelect={(day) => onSubmit(day)}
                  onBack={() => setSelectedMonth(false)}
                />
              )}
            </DropdownCard>
          </div>
        )}
      </div>
    </div>
  );
};

export default Calendar;
